import React, { useEffect, useState } from 'react';
import { db } from '../firebase-config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Seo from '../utils/Seo';
import Footer from './Footer';
import Loader from './Loader';
import { getEmbedUrl } from '../utils/videoUtils';

function VideoCard({ video, onPlay, isActive }) {
  // Determine if it's a reel aspect ratio
  const isReel = video.type === 'reel';
  
  return (
    <div className={`group relative w-full rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer bg-black ${isReel ? 'aspect-[9/16] max-w-[400px] mx-auto' : 'aspect-video'}`}>
      {!isActive ? (
        <>
          <img 
            src={video.thumbnail} 
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 group-hover:scale-110 transition-all duration-500 border border-white/30">
              <svg className="w-6 h-6 md:w-8 md:h-8 text-white fill-current translate-x-0.5" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
            <h3 className="text-[10px] md:text-[11px] tracking-[0.3em] font-black uppercase text-white">
              {video.title}
            </h3>
          </div>
        </>
      ) : (
        <>
          <iframe
            title={video.title}
            src={video.embed}
            className="absolute inset-0 w-full h-full border-0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onPlay(null);
            }}
            className="absolute top-4 right-4 md:top-6 md:right-6 bg-black/70 hover:bg-black text-white text-[8px] md:text-[9px] px-3 py-1.5 md:px-4 md:py-2 rounded-full uppercase tracking-[0.2em] font-bold transition-colors z-20 backdrop-blur-sm border border-white/20"
          >
            ✕ {video.type === 'reel' ? '' : 'Zatvori'}
          </button>
        </>
      )}
    </div>
  );
}

export default function FilmsPage() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'bs';
  const [films, setFilms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      const startTime = Date.now();
      try {
        const [filmsSnap, settingsSnap] = await Promise.all([
          getDocs(query(collection(db, 'films'), orderBy('createdAt', 'desc'))),
          getDocs(collection(db, 'settings'))
        ]);
        
        const items = filmsSnap.docs.map(d => ({ ...d.data(), id: d.id }));
        setFilms(items);

        if (!settingsSnap.empty) {
          setSettings(settingsSnap.docs[0].data());
        }

        console.log('[DEBUG] films from Firestore:', items.length);
      } catch (err) { 
        console.error('Error loading films', err);
        setError(err.message);
      } finally {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 800 - elapsed);
        setTimeout(() => setLoading(false), delay);
      }
    };
    
    load();
    
    // Safety timeout - don't stay in loader forever
    const timer = setTimeout(() => {
      setLoading(false);
    }, 8000);
    
    return () => clearTimeout(timer);
  }, []);

  const filmsTitle = currentLang === 'en' 
    ? (settings?.filmsTitleEn || 'Watch us in action')
    : (settings?.filmsTitleBs || 'Pogledajte nas u akciji');
  
  const filmsDescription = currentLang === 'en'
    ? (settings?.filmsDescriptionEn || 'Every video is a story - the story of your day told through our lens.')
    : (settings?.filmsDescriptionBs || 'Svaki video je priča - priča vašeg dana ispričana kroz našu kameru.');

  const videos = films.map(f => {
    const url = (f.url || '').trim();
    if (!url) return null;
    const embed = getEmbedUrl(url);
    if (!embed) return null;

    return { 
      ...f, 
      embed,
      thumbnail: f.thumbnail || '/img/photos/default.jpg'
    };
  }).filter(Boolean);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  if (loading) {
    return <Loader label={i18n.language === 'bs' ? 'Učitavanje filmova...' : 'Loading films...'} />;
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      <Seo 
        title={i18n.language === 'bs' ? `${filmsTitle} | Nejira Zukić` : `${filmsTitle} | Nejira Zukić`}
        description={filmsDescription}
      />

      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-between px-8 md:px-16 lg:px-20 py-8 md:py-10 bg-white/95 backdrop-blur-xl border-b border-gray-100">
        <Link to="/" className="text-xl md:text-2xl tracking-[0.4em] font-light text-gray-900">
          NEJIRA ZUKIĆ
        </Link>
        
        <button 
          onClick={toggleMenu} 
          className="text-[10px] md:text-xs tracking-[0.3em] font-bold uppercase text-gray-900 hover:opacity-60 transition-all"
        >
          {isMenuOpen ? (i18n.language === 'bs' ? 'ZATVORI' : 'CLOSE') : 'MENU'}
        </button>
      </header>

      {/* HERO SECTION */}
      <section className="pt-40 pb-24 px-6 md:px-12 text-center bg-gradient-to-b from-gray-50 to-white mb-20">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-400 mb-4">
              {i18n.language === 'bs' ? 'Video Materijali' : 'Video Material'}
            </p>
            <h1 className="text-4xl md:text-6xl font-serif italic text-gray-900 leading-tight">
              {filmsTitle}
            </h1>
          </div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {filmsDescription}
          </p>
        </div>
      </section>

      {/* VIDEOS GRID */}
      <main className="max-w-7xl mx-auto px-6 pb-32">
        {error ? (
          <div className="text-center py-20">
            <p className="text-red-400 text-sm font-bold mb-4">
              {i18n.language === 'bs' ? 'Greška pri učitavanju podataka' : 'Error loading data'}
            </p>
            <p className="text-gray-400 text-xs">{error}</p>
          </div>
        ) : videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {videos.map((video) => (
              <div key={video.id} onClick={() => setActiveVideo(video.id)}>
                <VideoCard 
                  video={video}
                  onPlay={setActiveVideo}
                  isActive={activeVideo === video.id}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-sm uppercase tracking-widest font-black mb-4">
              {i18n.language === 'bs' ? 'Nema dostupnih videoa' : 'No videos available'}
            </p>
            {films.length > 0 && (
              <p className="text-gray-300 text-[10px]">
                (Dostupno {films.length} stavki, ali format nije podržan)
              </p>
            )}
          </div>
        )}
      </main>

      {/* MENU OVERLAY */}
      <div 
        className={`fixed inset-0 z-[100] transition-all duration-400 ease-in-out ${
          isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 invisible'
        }`}
      >
        <div className="absolute inset-0 bg-white/40 backdrop-blur-xl" />
        
        <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
          <button 
            onClick={() => setIsMenuOpen(false)}
            className="absolute top-10 right-10 text-[10px] md:text-xs tracking-[0.4em] font-bold uppercase hover:opacity-50 transition-all duration-300 text-black border-none bg-transparent"
          >
            {i18n.language === 'bs' ? 'Zatvori' : 'Close'}
          </button>

          <nav className="space-y-6 md:space-y-10 mb-12 text-black">
            <Link 
              to="/" 
              onClick={() => setIsMenuOpen(false)}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Naslovna' : 'Home'}
              </span>
            </Link>
            <Link 
              to="/#portfolio" 
              onClick={() => setIsMenuOpen(false)}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                Portfolio
              </span>
            </Link>
            <Link 
              to="/films" 
              onClick={() => setIsMenuOpen(false)}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 italic">
                {i18n.language === 'bs' ? 'Filmovi' : 'Films'}
              </span>
            </Link>
            <Link 
              to="/#contact" 
              onClick={() => setIsMenuOpen(false)}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Kontakt' : 'Contact'}
              </span>
            </Link>
          </nav>

          {/* Language Selection */}
          <div className="flex items-center space-x-8 mb-12">
            <button 
              onClick={() => i18n.changeLanguage('bs')} 
              className={`text-[10px] md:text-xs tracking-[0.3em] transition-all duration-300 ${i18n.language === 'bs' ? 'font-bold border-b border-black' : 'font-light text-gray-400 hover:text-black'}`}
            >
              BS
            </button>
            <button 
              onClick={() => i18n.changeLanguage('en')} 
              className={`text-[10px] md:text-xs tracking-[0.3em] transition-all duration-300 ${i18n.language === 'en' ? 'font-bold border-b border-black' : 'font-light text-gray-400 hover:text-black'}`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
